import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useWebSocket, NotificationChannel, NotificationType } from '../contexts/WebSocketContext';
import { ConnectionStatus } from '../components/NotificationCenter';
import {
  Shield, Cloud, AlertTriangle, CheckCircle, TrendingUp,
  Server, Search, RefreshCw, Plus, ArrowRight, Activity,
  BarChart3, PieChart, FileText, Download, Database, Eye,
  ChevronDown, ChevronRight, Filter, X, Play, Clock, 
  AlertCircle, Info, Zap, Settings, ToggleLeft, ToggleRight,
  Rocket
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Switch } from '@/components/ui/switch';
import { toast } from 'sonner';
import { dashboardAPI, tenantsAPI, evidenceAPI, complianceAPI, severityColors, providerIcons, categoryIcons, frameworkShortNames } from '../services/amidala';
import { getFrameworks } from '../services/grc';
import DemoModeToggle from '../components/DemoModeToggle';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip as ReTooltip, ResponsiveContainer, Legend } from 'recharts';

// Helper hook for CloudConfigurationGuide
const useNavigateHelper = () => useNavigate();

const AmidalaDashboard = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const basePath = location.pathname.startsWith('/amidala') ? '/amidala' : '/grc/amidala';
  const { subscribe, addListener, isConnected, alerts } = useWebSocket();
  const [loading, setLoading] = useState(true);
  const [seeding, setSeeding] = useState(false);
  const [error, setError] = useState(null);
  const [tenant, setTenant] = useState(null);
  const [dashboard, setDashboard] = useState(null);
  const [showCreateTenant, setShowCreateTenant] = useState(false);
  const [newTenantName, setNewTenantName] = useState('');
  const [useSampleData, setUseSampleData] = useState(true);
  const [expandedFinding, setExpandedFinding] = useState(null);
  const [collectingEvidence, setCollectingEvidence] = useState(null);
  // Framework-mapping modal state
  const [evidenceModalScan, setEvidenceModalScan] = useState(null); // {id, name} of the scan to map
  const [availableFrameworks, setAvailableFrameworks] = useState([]);
  const [selectedFrameworkCodes, setSelectedFrameworkCodes] = useState([]);
  const [loadingFrameworks, setLoadingFrameworks] = useState(false);

  // Subscribe to Amidala and Alerts channels
  useEffect(() => {
    subscribe([NotificationChannel.AMIDALA, NotificationChannel.ALERTS]);
    
    // Listen for critical alerts to trigger dashboard refresh
    const cleanup = addListener(NotificationType.CRITICAL_ALERT, () => {
      loadData(); // Refresh dashboard when critical alert comes in
    });
    
    return cleanup;
  }, [subscribe, addListener]);

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      // Only show full-screen loading on initial mount; background refreshes (demo toggle,
      // alert-driven refresh, etc.) keep the dashboard tree mounted so transient overlays
      // like the Collect Evidence modal don't get unmounted mid-interaction.
      if (!dashboard) setLoading(true);
      setError(null);

      const tenantsRes = await tenantsAPI.list();
      let currentTenant = tenantsRes.data[0];

      if (!currentTenant) {
        setShowCreateTenant(true);
        setLoading(false);
        return;
      }

      setTenant(currentTenant);
      const dashboardRes = await dashboardAPI.get(currentTenant.id);
      setDashboard(dashboardRes.data);
    } catch (err) {
      console.error('Error loading dashboard:', err);
      setError(err.message || 'Failed to load dashboard');
    } finally {
      setLoading(false);
    }
  };

  // Current user (from localStorage) — used for super-admin gate on the
  // framework rename dialog + as the localStorage key for the auto-saved
  // chip filter.
  const _currentUser = React.useMemo(() => {
    try { return JSON.parse(localStorage.getItem('user') || 'null'); }
    catch (_e) { return null; }
  }, []);

  // Compliance coverage state
  const [complianceCoverage, setComplianceCoverage] = useState([]);
  const [enabledFrameworks, setEnabledFrameworks] = useState([]);
  const [frameworkLabels, setFrameworkLabels] = useState({});
  // Framework rename dialog (super-admin only)
  const [showRenameDialog, setShowRenameDialog] = useState(false);
  const [renameDraft, setRenameDraft] = useState({});
  const [savingLabels, setSavingLabels] = useState(false);
  // Selected framework filter (both tiles share the same picker). Empty set
  // means "show all enabled frameworks". Persisted to localStorage per
  // (userId, tenantId) so the dashboard remembers each user's view.
  const _filterStorageKey = (tenantId) => {
    const uid = (JSON.parse(localStorage.getItem('user') || '{}') || {}).id || 'anon';
    return `amidala:filter:${uid}:${tenantId || 'notenant'}`;
  };
  const [selectedFrameworkFilter, setSelectedFrameworkFilter] = useState(new Set());
  const _saveFilterToStorage = (tenantId, set) => {
    try {
      localStorage.setItem(_filterStorageKey(tenantId), JSON.stringify([...set]));
    } catch (_e) { /* localStorage full or blocked — best-effort */ }
  };
  const _loadFilterFromStorage = (tenantId) => {
    try {
      const raw = localStorage.getItem(_filterStorageKey(tenantId));
      if (raw) return new Set(JSON.parse(raw));
    } catch (_e) { /* ignore */ }
    return new Set();
  };
  const _updateFilter = (updater) => {
    setSelectedFrameworkFilter((prev) => {
      const next = typeof updater === 'function' ? updater(prev) : updater;
      _saveFilterToStorage(tenant?.id, next);
      return next;
    });
  };
  // Preset bundles — hard-coded common-industry framework sets. When applied
  // we intersect with the tenant's enabledFrameworks so no chip is set that
  // isn't already enabled by the tenant.
  const CHIP_PRESETS = [
    { key: 'financial', label: 'Regulated Financial', frameworks: ['SOC2', 'ISO27001', 'PCI-DSS', 'NIST', 'CIS'] },
    { key: 'healthcare', label: 'Healthcare', frameworks: ['HIPAA', 'SOC2', 'ISO27001', 'NIST'] },
    { key: 'saas', label: 'SaaS Startup', frameworks: ['SOC2', 'GDPR', 'ISO27001'] },
  ];
  const applyPreset = (presetKey) => {
    const p = CHIP_PRESETS.find((x) => x.key === presetKey);
    if (!p) return;
    const enabledSet = new Set(enabledFrameworks);
    const applied = new Set(p.frameworks.filter((f) => enabledSet.has(f)));
    _updateFilter(applied);
    toast.success(`Applied preset: ${p.label} (${applied.size} frameworks)`);
  };
  const [showPresetMenu, setShowPresetMenu] = useState(false);
  const [loadingCoverage, setLoadingCoverage] = useState(false);
  const [selectedFrameworkForPdf, setSelectedFrameworkForPdf] = useState(null);
  const [showComplianceExportDialog, setShowComplianceExportDialog] = useState(false);
  const [exportingComplianceReport, setExportingComplianceReport] = useState(false);
  // Coverage trend state
  const [coverageTrend, setCoverageTrend] = useState({});
  const [trendFramework, setTrendFramework] = useState('');
  const [trendWeeks] = useState(12);
  const [snapshottingCoverage, setSnapshottingCoverage] = useState(false);

  const loadCoverageTrend = async (tenantId) => {
    if (!tenantId) return;
    try {
      const res = await complianceAPI.getTrend(tenantId, trendWeeks);
      setCoverageTrend(res.data.series || {});
      if (Array.isArray(res.data.enabled_frameworks) && res.data.enabled_frameworks.length > 0) {
        setEnabledFrameworks((prev) => prev.length ? prev : res.data.enabled_frameworks);
      }
      if (res.data.framework_labels && typeof res.data.framework_labels === 'object') {
        setFrameworkLabels((prev) => Object.keys(prev).length ? prev : res.data.framework_labels);
      }
    } catch (err) {
      // trend is optional; ignore
    }
  };

  const takeCoverageSnapshot = async () => {
    if (!tenant?.id) return;
    try {
      setSnapshottingCoverage(true);
      const res = await complianceAPI.snapshot(tenant.id);
      toast.success(`Snapshot taken for ${res.data.frameworks_snapshotted} frameworks on ${res.data.snapshot_date}`);
      loadCoverageTrend(tenant.id);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Snapshot failed');
    } finally {
      setSnapshottingCoverage(false);
    }
  };

  const loadComplianceCoverage = async (tenantId) => {
    if (!tenantId) return;
    try {
      setLoadingCoverage(true);
      const res = await complianceAPI.getCoverage(tenantId);
      setComplianceCoverage(res.data.coverage || []);
      if (Array.isArray(res.data.enabled_frameworks)) {
        setEnabledFrameworks(res.data.enabled_frameworks);
      }
      if (res.data.framework_labels && typeof res.data.framework_labels === 'object') {
        setFrameworkLabels(res.data.framework_labels);
      }
      // Restore this user's saved chip filter on tenant load
      _updateFilter(_loadFilterFromStorage(tenantId));
    } catch (err) {
      console.error('Failed to load compliance coverage:', err);
    } finally {
      setLoadingCoverage(false);
    }
  };

  const exportComplianceReportPdf = async () => {
    if (!tenant?.id || !selectedFrameworkForPdf) {
      toast.error('Select a framework first');
      return;
    }
    try {
      setExportingComplianceReport(true);
      const res = await complianceAPI.exportPdf(tenant.id, selectedFrameworkForPdf);
      const url = window.URL.createObjectURL(new Blob([res.data], { type: 'application/pdf' }));
      const a = document.createElement('a');
      a.href = url;
      a.download = `amidala-compliance-${selectedFrameworkForPdf}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
      toast.success('Compliance report downloaded');
      setShowComplianceExportDialog(false);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to export report');
    } finally {
      setExportingComplianceReport(false);
    }
  };

  useEffect(() => {
    if (tenant?.id) {
      loadComplianceCoverage(tenant.id);
      loadCoverageTrend(tenant.id);
    }
  }, [tenant?.id]);

  const handleCreateTenant = async () => {
    if (!newTenantName.trim()) return;
    
    try {
      const res = await tenantsAPI.create({ name: newTenantName, description: 'My organization' });
      setTenant(res.data);
      setShowCreateTenant(false);
      loadData();
    } catch (err) {
      setError('Failed to create organization');
    }
  };

  const handleSeedSampleData = async () => {
    try {
      setSeeding(true);
      setError(null);
      await tenantsAPI.seedSampleData();
      await loadData();
    } catch (err) {
      setError('Failed to seed sample data');
    } finally {
      setSeeding(false);
    }
  };

  const toggleSampleData = async () => {
    setUseSampleData(!useSampleData);
    if (!useSampleData) {
      await handleSeedSampleData();
    }
  };

  // Calculate derived metrics
  const stats = dashboard || {};
  const totalFindings = stats.total_findings || 0;
  // Security Score = (passed_checks / total_checks) * 100 from the latest scan
  // (backend already computes this). Fall back to derived ratio for safety.
  const totalChecksLatest = stats.total_checks_latest || 0;
  const passedFindings = stats.passed_checks_latest ?? (stats.passed_findings || 0);
  const failedFindings = stats.failed_checks_latest ?? (stats.failed_findings || 0);
  const threatScore = stats.security_score != null
    ? Number(stats.security_score).toFixed(2)
    : (totalChecksLatest > 0
        ? ((passedFindings / totalChecksLatest) * 100).toFixed(2)
        : 0);
  
  const severityData = {
    critical: stats.severity_distribution?.critical || stats.critical_findings || 0,
    high: stats.severity_distribution?.high || stats.high_findings || 0,
    medium: stats.severity_distribution?.medium || 0,
    low: stats.severity_distribution?.low || 0,
    info: stats.severity_distribution?.info || 0,
  };
  
  const totalSeverity = Object.values(severityData).reduce((a, b) => a + b, 0) || 1;

  // Service watchlist from findings_by_service (real data). Empty when no findings yet.
  const serviceWatchlist = stats.findings_by_service
    ? Object.entries(stats.findings_by_service)
        .map(([name, count]) => ({ name, count: typeof count === 'number' ? count : 0 }))
        .filter((s) => s.count > 0)
        .sort((a, b) => b.count - a.count)
        .slice(0, 6)
    : [];

  // Latest failing findings (real data only — no hardcoded fallback)
  const latestFindings = stats.top_failing_checks?.slice(0, 8) || [];

  // Provider breakdown
  const providerBreakdown = stats.findings_by_provider || { aws: totalFindings * 0.7, azure: totalFindings * 0.2, gcp: totalFindings * 0.1 };

  const handleCollectEvidence = async (scanId, e) => {
    e.stopPropagation();
    // Open framework mapping modal first
    const scan = (stats.recent_scans || []).find((s) => s.id === scanId);
    setEvidenceModalScan({ id: scanId, name: scan?.name || 'Scan' });
    setSelectedFrameworkCodes([]);
    setLoadingFrameworks(true);
    try {
      const data = await getFrameworks({ page: 1, page_size: 100 });
      const items = data?.items || [];
      setAvailableFrameworks(items);
      // Pre-select all enabled frameworks by default
      setSelectedFrameworkCodes(items.map((f) => f.code).filter(Boolean));
    } catch (err) {
      console.error('Failed to load frameworks', err);
      setAvailableFrameworks([]);
    } finally {
      setLoadingFrameworks(false);
    }
  };

  const toggleFrameworkSelection = (code) => {
    setSelectedFrameworkCodes((prev) =>
      prev.includes(code) ? prev.filter((c) => c !== code) : [...prev, code]
    );
  };

  const confirmCollectEvidence = async () => {
    if (!evidenceModalScan) return;
    const scanId = evidenceModalScan.id;
    setCollectingEvidence(scanId);
    try {
      // ``evidenceAPI.collectFromScan`` goes through the shared amidala axios
      // client, so the auth token + active tenant (``X-Tenant-ID``) are
      // attached automatically by the interceptor.
      const res = await evidenceAPI.collectFromScan(scanId, selectedFrameworkCodes);
      const data = res.data || {};
      toast.success(
        `Evidence collected: ${data.evidence_created} records from ${data.checks_processed} checks` +
          (selectedFrameworkCodes.length ? ` mapped to ${selectedFrameworkCodes.length} framework(s)` : '')
      );
      setEvidenceModalScan(null);
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Failed to collect evidence');
    } finally {
      setCollectingEvidence(null);
    }
  };

  const closeEvidenceModal = () => {
    if (collectingEvidence) return;
    setEvidenceModalScan(null);
    setAvailableFrameworks([]);
    setSelectedFrameworkCodes([]);
  };

  if (loading) {
    return (
      <>
        <div className="min-h-screen bg-slate-900 flex items-center justify-center">
          <div className="text-center">
            <RefreshCw className="w-12 h-12 animate-spin text-emerald-500 mx-auto mb-4" />
            <p className="text-slate-400">Loading security posture data...</p>
          </div>
        </div>
        {/* Modal kept mounted across loading/refresh transitions */}
        <EvidenceFrameworkModal
          evidenceModalScan={evidenceModalScan}
          availableFrameworks={availableFrameworks}
          selectedFrameworkCodes={selectedFrameworkCodes}
          loadingFrameworks={loadingFrameworks}
          collectingEvidence={collectingEvidence}
          toggleFrameworkSelection={toggleFrameworkSelection}
          setSelectedFrameworkCodes={setSelectedFrameworkCodes}
          closeEvidenceModal={closeEvidenceModal}
          confirmCollectEvidence={confirmCollectEvidence}
        />
      </>
    );
  }

  if (showCreateTenant) {
    return (
      <div className="min-h-screen bg-slate-900 flex items-center justify-center p-4">
        <Card className="w-full max-w-lg bg-slate-800 border-slate-700">
          <CardHeader className="text-center border-b border-slate-700">
            <div className="w-16 h-16 bg-emerald-500/20 rounded-xl flex items-center justify-center mx-auto mb-4">
              <Shield className="w-10 h-10 text-emerald-500" />
            </div>
            <CardTitle className="text-2xl text-white">Welcome to Amidala CSPM</CardTitle>
            <p className="text-slate-400 mt-2">Cloud Security Posture Management</p>
          </CardHeader>
          <CardContent className="pt-6 space-y-6">
            {/* Quick Start with Sample Data */}
            <div className="p-4 bg-gradient-to-r from-emerald-500/10 to-teal-500/10 rounded-lg border border-emerald-500/20">
              <h3 className="font-semibold text-emerald-400 mb-2 flex items-center gap-2">
                <Database className="w-5 h-5" />
                Quick Start - Demo Mode
              </h3>
              <p className="text-sm text-slate-400 mb-3">
                Load sample cloud accounts, security findings, and compliance data to explore the dashboard.
              </p>
              <Button
                onClick={handleSeedSampleData}
                disabled={seeding}
                className="w-full bg-emerald-600 hover:bg-emerald-700 text-white"
              >
                {seeding ? (
                  <>
                    <RefreshCw className="w-4 h-4 animate-spin mr-2" />
                    Loading Demo Data...
                  </>
                ) : (
                  <>
                    <Play className="w-4 h-4 mr-2" />
                    Launch Demo Dashboard
                  </>
                )}
              </Button>
            </div>

            <div className="relative">
              <div className="absolute inset-0 flex items-center">
                <div className="w-full border-t border-slate-600"></div>
              </div>
              <div className="relative flex justify-center text-sm">
                <span className="px-2 bg-slate-800 text-slate-500">or create organization</span>
              </div>
            </div>
            
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-slate-300 mb-1">
                  Organization Name
                </label>
                <input
                  type="text"
                  value={newTenantName}
                  onChange={(e) => setNewTenantName(e.target.value)}
                  placeholder="My Organization"
                  className="w-full px-4 py-2 bg-slate-700 border border-slate-600 rounded-lg text-white placeholder-slate-400 focus:ring-2 focus:ring-emerald-500 focus:border-transparent"
                />
              </div>
              
              <Button
                onClick={handleCreateTenant}
                variant="outline"
                className="w-full border-slate-600 text-slate-300 hover:bg-slate-700"
              >
                Create Empty Organization
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  const hasData = stats.total_accounts > 0 || stats.total_scans > 0;

  return (
    <div className="min-h-screen bg-slate-900 text-white">
      {/* Header */}
      <div className="border-b border-slate-700 bg-slate-800/50 backdrop-blur sticky top-0 z-10">
        <div className="max-w-[1800px] mx-auto px-6 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-emerald-500/20 rounded-lg flex items-center justify-center">
                  <Shield className="w-6 h-6 text-emerald-500" />
                </div>
                <div>
                  <h1 className="text-xl font-bold text-white">Amidala CSPM</h1>
                  <p className="text-xs text-slate-400">Security Posture Overview</p>
                </div>
              </div>
            </div>
            
            <div className="flex items-center gap-4">
              {/* Connection Status */}
              <ConnectionStatus className="px-2 py-1 bg-slate-700/30 rounded" />
              
              {/* Demo Mode Toggle (tenant-level, defaults OFF) */}
              {tenant && (
                <DemoModeToggle
                  tenantId={tenant.id}
                  onChange={() => loadData()}
                />
              )}
              {seeding && <RefreshCw className="w-4 h-4 animate-spin text-emerald-500" />}
              
              <Button
                onClick={() => navigate(`${basePath}/scan`)}
                className="bg-emerald-600 hover:bg-emerald-700 text-white"
                data-testid="launch-scan-btn"
              >
                <Zap className="w-4 h-4 mr-2" />
                Launch Scan
              </Button>
            </div>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="max-w-[1800px] mx-auto px-6 py-6 space-y-6">
        {/* Show Configuration Guide when Demo Mode is OFF */}
        {!useSampleData ? (
          <CloudConfigurationGuide onEnableDemo={() => setUseSampleData(true)} />
        ) : (
          <>
        {/* Filter Bar */}
        <div className="flex items-center gap-4 flex-wrap">
          <div className="flex items-center gap-2 px-3 py-2 bg-slate-800 rounded-lg border border-slate-700">
            <Filter className="w-4 h-4 text-slate-400" />
            <select className="bg-transparent text-sm text-slate-300 focus:outline-none">
              <option>All Providers</option>
              <option>AWS</option>
              <option>Azure</option>
              <option>GCP</option>
            </select>
          </div>
          <div className="flex items-center gap-2 px-3 py-2 bg-slate-800 rounded-lg border border-slate-700">
            <Clock className="w-4 h-4 text-slate-400" />
            <select className="bg-transparent text-sm text-slate-300 focus:outline-none">
              <option>Last 7 days</option>
              <option>Last 30 days</option>
              <option>Last 90 days</option>
            </select>
          </div>
          <Button
            variant="ghost"
            size="sm"
            onClick={loadData}
            className="text-slate-400 hover:text-white"
          >
            <RefreshCw className="w-4 h-4 mr-2" />
            Refresh
          </Button>
        </div>

        {/* Top Row — Latest Scan | Risk Severity Donut | Top Frameworks by Coverage
            (3-widget hero replacing the older 4-tile grid) */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6" data-testid="dashboard-hero-v2">
          {/* Widget 1: Latest Scan Card — big security score + delta + view CTA */}
          {(() => {
            const recent = stats.recent_scans || [];
            const latest = recent[0] || {};
            const prev = recent[1] || {};
            const currentScore = Number(threatScore);
            const prevScore = Number(prev.security_score || 0);
            const delta = latest.id && prev.id ? (currentScore - prevScore) : null;
            const scoreColor = currentScore >= 80 ? '#22c55e' : currentScore >= 60 ? '#eab308' : '#ef4444';
            return (
              <Card className="bg-slate-800 border-slate-700" data-testid="hero-latest-scan-card">
                <CardContent className="pt-6 h-full flex flex-col">
                  <div className="flex items-center justify-between mb-2">
                    <h3 className="text-sm font-medium text-slate-400">Latest Scan</h3>
                    <Badge variant="outline" className="text-emerald-400 border-emerald-400/30 text-xs" data-testid="hero-latest-scan-grade">
                      {currentScore >= 80 ? 'Good' : currentScore >= 60 ? 'Fair' : 'Poor'}
                    </Badge>
                  </div>
                  <div className="flex items-center gap-6 flex-1">
                    <div className="relative w-32 h-32 flex-shrink-0">
                      <svg className="w-full h-full transform -rotate-90" viewBox="0 0 100 100">
                        <circle cx="50" cy="50" r="45" fill="none" stroke="#334155" strokeWidth="8" />
                        <circle
                          cx="50" cy="50" r="45" fill="none"
                          stroke={scoreColor}
                          strokeWidth="8"
                          strokeLinecap="round"
                          strokeDasharray={`${currentScore * 2.83} 283`}
                          data-testid="hero-latest-scan-ring"
                        />
                      </svg>
                      <div className="absolute inset-0 flex items-center justify-center">
                        <div className="text-center">
                          <span className="text-3xl font-bold text-white" data-testid="hero-latest-scan-score">
                            {currentScore.toFixed(0)}
                          </span>
                          <p className="text-[10px] text-slate-400">Security Score</p>
                        </div>
                      </div>
                    </div>
                    <div className="flex-1 min-w-0 space-y-2">
                      {delta !== null && Math.abs(delta) >= 0.01 && (
                        <div className={`inline-flex items-center gap-1 text-xs font-medium px-2 py-1 rounded-full ${
                          delta > 0 ? 'bg-emerald-500/10 text-emerald-400' : 'bg-rose-500/10 text-rose-400'
                        }`} data-testid="hero-latest-scan-delta">
                          {delta > 0 ? '▲' : '▼'} {Math.abs(delta).toFixed(1)} pts vs previous
                        </div>
                      )}
                      <p className="text-xs text-slate-400">
                        <span className="text-white font-medium">{stats.total_checks_latest || 0}</span> checks ·{' '}
                        <span className="text-emerald-400 font-medium">{stats.passed_checks_latest || 0}</span> passed ·{' '}
                        <span className="text-rose-400 font-medium">{stats.failed_checks_latest || 0}</span> failed
                      </p>
                      {stats.latest_scan_at && (
                        <p className="text-[11px] text-slate-500" data-testid="hero-latest-scan-time">
                          {new Date(stats.latest_scan_at).toLocaleString()}
                        </p>
                      )}
                      {stats.latest_scan_id && (
                        <Button
                          size="sm"
                          className="bg-indigo-600 hover:bg-indigo-700 text-white text-xs mt-1"
                          data-testid="hero-latest-scan-view-btn"
                          onClick={() => navigate(`/amidala/scans/${stats.latest_scan_id}`)}
                        >
                          View Latest Scan
                          <ArrowRight className="w-3 h-3 ml-1" />
                        </Button>
                      )}
                    </div>
                  </div>
                </CardContent>
              </Card>
            );
          })()}

          {/* Widget 2: Risk Severity Donut — Critical/High/Medium/Low breakdown */}
          {(() => {
            const critical = severityData.critical;
            const high = severityData.high;
            const medium = severityData.medium;
            const low = severityData.low;
            const total = critical + high + medium + low;
            const seg = (v) => (total > 0 ? (v / total) * 283 : 0);
            // cumulative offsets for the stacked donut segments
            const oCrit = 0;
            const oHigh = seg(critical);
            const oMed = seg(critical) + seg(high);
            const oLow = seg(critical) + seg(high) + seg(medium);
            return (
              <Card className="bg-slate-800 border-slate-700" data-testid="hero-risk-severity-donut">
                <CardContent className="pt-6 h-full">
                  <div className="flex items-center justify-between mb-4">
                    <h3 className="text-sm font-medium text-slate-400">Risk Severity</h3>
                    <span className="text-xs text-slate-500">Open findings</span>
                  </div>
                  <div className="flex items-center gap-4">
                    <div className="relative w-32 h-32 flex-shrink-0">
                      <svg className="w-full h-full transform -rotate-90" viewBox="0 0 100 100">
                        <circle cx="50" cy="50" r="45" fill="none" stroke="#334155" strokeWidth="14" />
                        {total > 0 && (
                          <>
                            <circle cx="50" cy="50" r="45" fill="none" stroke="#ec4899" strokeWidth="14"
                              strokeDasharray={`${seg(critical)} 283`} strokeDashoffset={-oCrit} data-testid="donut-critical" />
                            <circle cx="50" cy="50" r="45" fill="none" stroke="#f97316" strokeWidth="14"
                              strokeDasharray={`${seg(high)} 283`} strokeDashoffset={-oHigh} data-testid="donut-high" />
                            <circle cx="50" cy="50" r="45" fill="none" stroke="#eab308" strokeWidth="14"
                              strokeDasharray={`${seg(medium)} 283`} strokeDashoffset={-oMed} data-testid="donut-medium" />
                            <circle cx="50" cy="50" r="45" fill="none" stroke="#94a3b8" strokeWidth="14"
                              strokeDasharray={`${seg(low)} 283`} strokeDashoffset={-oLow} data-testid="donut-low" />
                          </>
                        )}
                      </svg>
                      <div className="absolute inset-0 flex items-center justify-center">
                        <div className="text-center">
                          <span className="text-2xl font-bold text-white" data-testid="donut-total">{total}</span>
                          <p className="text-[10px] text-slate-400">total</p>
                        </div>
                      </div>
                    </div>
                    <div className="flex-1 min-w-0 space-y-2">
                      {[
                        { label: 'Critical', v: critical, color: 'bg-pink-500', text: 'text-pink-400' },
                        { label: 'High', v: high, color: 'bg-orange-500', text: 'text-orange-400' },
                        { label: 'Medium', v: medium, color: 'bg-yellow-500', text: 'text-yellow-400' },
                        { label: 'Low', v: low, color: 'bg-slate-400', text: 'text-slate-300' },
                      ].map(({ label, v, color, text }) => (
                        <div key={label} className="flex items-center gap-2" data-testid={`sev-row-${label.toLowerCase()}`}>
                          <span className={`w-2.5 h-2.5 rounded-full ${color}`} />
                          <span className="text-xs text-slate-300 flex-1">{label}</span>
                          <span className={`text-xs font-medium ${text}`}>{v}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                </CardContent>
              </Card>
            );
          })()}

          {/* Widget 3: Top 5 Frameworks by Coverage */}
          {(() => {
            const raw = stats.compliance_by_framework || {};
            const PINNED_MATCHERS = ['ISO27001', 'SOC2', 'NIST80053'];
            const normFw = (s) => String(s || '').toUpperCase().replace(/[^A-Z0-9]/g, '');
            const isPinned = (name) => PINNED_MATCHERS.some((m) => normFw(name).includes(m));
            const items = Object.entries(raw)
              .map(([name, score]) => ({ name, score: Number(score) || 0, pinned: isPinned(name) }));
            // Sort pinned first, then by coverage desc; take top 5.
            items.sort((a, b) => {
              if (a.pinned !== b.pinned) return a.pinned ? -1 : 1;
              return b.score - a.score;
            });
            const rows = items.slice(0, 5);
            return (
              <Card className="bg-slate-800 border-slate-700" data-testid="hero-top-frameworks">
                <CardContent className="pt-6 h-full flex flex-col">
                  <div className="flex items-center justify-between mb-4">
                    <h3 className="text-sm font-medium text-slate-400">Compliance by Framework</h3>
                    <span className="text-xs text-slate-500">{rows.length} of {Object.keys(raw).length}</span>
                  </div>
                  {rows.length === 0 ? (
                    <div className="flex-1 flex items-center justify-center text-xs text-slate-500 text-center px-4"
                         data-testid="hero-top-frameworks-empty">
                      Enable frameworks + run a scan to populate compliance coverage here.
                    </div>
                  ) : (
                    <div className="space-y-2.5 flex-1" data-testid="hero-top-frameworks-list">
                      {rows.map((r, i) => {
                        const pct = Math.min(100, Math.max(0, r.score));
                        const barColor = pct >= 80 ? 'bg-emerald-500' : pct >= 50 ? 'bg-amber-500' : 'bg-rose-500';
                        return (
                          <div key={r.name} className="space-y-1" data-testid={`framework-row-${i}`}>
                            <div className="flex items-center justify-between text-xs">
                              <span className="text-slate-300 font-medium truncate flex items-center gap-1" title={r.name}>
                                {r.pinned && <span className="text-amber-400 text-[10px]" title="Auditor-focus framework">★</span>}
                                {r.name}
                              </span>
                              <span className="text-white font-semibold ml-2">{pct.toFixed(1)}%</span>
                            </div>
                            <div className="h-1.5 bg-slate-700 rounded-full overflow-hidden">
                              <div className={`h-full ${barColor} rounded-full transition-all`}
                                   style={{ width: `${pct}%` }} />
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  )}
                </CardContent>
              </Card>
            );
          })()}
        </div>

        {/* Compliance Coverage Tile */}
        <Card className="bg-slate-800 border-slate-700" data-testid="compliance-coverage-tile">
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-3">
            <div className="flex-1 min-w-0">
              <CardTitle className="text-lg text-white">Compliance Coverage</CardTitle>
              <p className="text-xs text-slate-400 mt-1">
                Top frameworks by coverage. Click Export to download an auditor-ready PDF.
              </p>
              {enabledFrameworks.length > 0 && (
                <div className="flex flex-wrap items-center gap-1.5 mt-2" data-testid="coverage-framework-chips">
                  {enabledFrameworks.map((fw) => {
                    const active = selectedFrameworkFilter.has(fw);
                    const label = frameworkLabels[fw] || fw;
                    return (
                      <button
                        key={fw}
                        type="button"
                        onClick={() => {
                          _updateFilter((prev) => {
                            const next = new Set(prev);
                            if (next.has(fw)) next.delete(fw); else next.add(fw);
                            return next;
                          });
                        }}
                        className={`text-[11px] px-2 py-0.5 rounded-full border transition ${
                          active
                            ? 'bg-indigo-600 border-indigo-500 text-white'
                            : 'bg-slate-900 border-slate-700 text-slate-300 hover:border-indigo-500 hover:text-white'
                        }`}
                        data-testid={`framework-chip-${fw}`}
                        title={fw === label ? undefined : `key: ${fw}`}
                      >
                        {label}
                      </button>
                    );
                  })}
                  {selectedFrameworkFilter.size > 0 && (
                    <button
                      type="button"
                      onClick={() => _updateFilter(new Set())}
                      className="text-[11px] px-2 py-0.5 rounded-full border border-slate-700 text-slate-400 hover:text-white"
                      data-testid="framework-chip-clear"
                    >
                      Clear
                    </button>
                  )}
                  {/* Preset picker */}
                  <div className="relative ml-1">
                    <button
                      type="button"
                      onClick={() => setShowPresetMenu((p) => !p)}
                      className="text-[11px] px-2 py-0.5 rounded-full border border-emerald-600/60 text-emerald-300 hover:bg-emerald-600/10"
                      data-testid="chip-preset-menu-toggle"
                    >
                      Presets ▾
                    </button>
                    {showPresetMenu && (
                      <div
                        className="absolute z-30 mt-1 left-0 w-52 rounded-lg border border-slate-700 bg-slate-900 shadow-xl py-1"
                        data-testid="chip-preset-menu"
                        onMouseLeave={() => setShowPresetMenu(false)}
                      >
                        {CHIP_PRESETS.map((p) => (
                          <button
                            key={p.key}
                            type="button"
                            onClick={() => { applyPreset(p.key); setShowPresetMenu(false); }}
                            className="w-full text-left px-3 py-1.5 text-xs text-slate-200 hover:bg-slate-800"
                            data-testid={`chip-preset-${p.key}`}
                          >
                            <div className="font-medium">{p.label}</div>
                            <div className="text-[10px] text-slate-500">{p.frameworks.join(' · ')}</div>
                          </button>
                        ))}
                      </div>
                    )}
                  </div>
                  {/* Rename dialog opener (super-admin only) */}
                  {_currentUser?.is_super_admin && (
                    <button
                      type="button"
                      onClick={() => {
                        // Seed the draft with current labels
                        const draft = {};
                        enabledFrameworks.forEach((fw) => { draft[fw] = frameworkLabels[fw] || ''; });
                        setRenameDraft(draft);
                        setShowRenameDialog(true);
                      }}
                      className="text-[11px] px-2 py-0.5 rounded-full border border-slate-700 text-slate-400 hover:text-white"
                      data-testid="framework-rename-btn"
                      title="Rename framework display labels (super-admin)"
                    >
                      Rename…
                    </button>
                  )}
                </div>
              )}
            </div>
            <Button
              size="sm"
              variant="outline"
              data-testid="export-compliance-report-btn"
              onClick={() => setShowComplianceExportDialog(true)}
              className="bg-indigo-600 hover:bg-indigo-700 text-white border-0"
            >
              📄 Export Compliance Report PDF
            </Button>
          </CardHeader>
          <CardContent>
            {loadingCoverage ? (
              <div className="text-slate-400 text-sm py-2">Loading coverage…</div>
            ) : complianceCoverage.length === 0 ? (
              <div className="text-slate-500 text-sm py-2" data-testid="compliance-coverage-empty">
                No compliance frameworks synced yet. Ask a super-admin to sync from
                <span className="mx-1 font-mono text-slate-300">/admin/vulnerability-db → Compliance</span>.
              </div>
            ) : (() => {
              const normalise = (s) => String(s || '').toUpperCase().replace(/[^A-Z0-9]/g, '');
              const active = selectedFrameworkFilter.size > 0
                ? new Set([...selectedFrameworkFilter].map(normalise))
                : null;
              const filtered = active
                ? complianceCoverage.filter((c) =>
                    active.has(normalise(c.framework)) || active.has(normalise(c.file_name)))
                : complianceCoverage;
              if (filtered.length === 0) {
                return (
                  <div className="text-slate-500 text-sm py-2" data-testid="coverage-filtered-empty">
                    No snapshots for the selected frameworks yet.
                  </div>
                );
              }
              return (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-3">
                  {filtered.slice(0, 8).map((c) => {
                    const noData = c.data_status === 'no_data';
                    const mappingGap = c.data_status === 'mapping_gap';
                    const neutral = noData || mappingGap;
                    const pct = neutral ? 0 : (c.coverage_pct || 0);
                    const barColor = neutral
                      ? 'bg-slate-600'
                      : pct >= 80 ? 'bg-emerald-500' : pct >= 50 ? 'bg-yellow-500' : 'bg-red-500';
                    return (
                      <button
                        key={c.file_name}
                        data-testid={`coverage-tile-${c.file_name}`}
                        onClick={() => { setSelectedFrameworkForPdf(c.file_name); setShowComplianceExportDialog(true); }}
                        className={`text-left p-3 rounded-lg border bg-slate-900 hover:border-indigo-500 transition ${
                          c.pinned ? 'border-amber-500/40 ring-1 ring-amber-500/20' : 'border-slate-700'
                        }`}
                      >
                        <div className="flex items-center justify-between mb-0.5">
                          <div className="text-xs text-slate-400 uppercase">{c.provider}</div>
                          {c.pinned && (
                            <span className="text-[10px] text-amber-400 font-medium inline-flex items-center gap-0.5"
                                  data-testid="coverage-pinned-badge"
                                  title="Auditor-focus framework — always pinned">
                              ★ Pinned
                            </span>
                          )}
                        </div>
                        <div className="text-sm font-semibold text-white truncate" title={c.framework}>{c.framework}</div>
                        <div className="text-xs text-slate-500 mb-2">{c.version || 'n/a'}</div>
                        <div className="h-2 bg-slate-700 rounded-full overflow-hidden">
                          <div className={`h-full ${barColor}`} style={{ width: neutral ? '100%' : `${pct}%`, opacity: neutral ? 0.3 : 1 }} />
                        </div>
                        <div className="flex justify-between mt-1">
                          {noData ? (
                            <>
                              <span className="text-xs text-slate-500 italic">No scan data</span>
                              <span className="text-xs font-medium text-slate-500" title="Run a scan against this provider to populate coverage">—</span>
                            </>
                          ) : mappingGap ? (
                            <>
                              <span
                                className="text-xs text-amber-400 italic"
                                data-testid={`coverage-mapping-gap-${c.file_name}`}
                                title="This framework's catalog does not match any of the tenant's open findings — we can't credibly report a pass rate. Extend the AMIDALA_TO_PROWLER mapping or ensure findings carry Prowler-native check IDs."
                              >
                                Mapping gap
                              </span>
                              <span className="text-xs font-medium text-amber-400" title="No open findings map to this framework's Checks list">—</span>
                            </>
                          ) : (
                            <>
                              <span className="text-xs text-slate-400">{c.passing_requirements}/{c.total_requirements}</span>
                              <span className="text-xs font-medium text-white">{pct}%</span>
                            </>
                          )}
                        </div>
                      </button>
                    );
                  })}
                </div>
              );
            })()}
          </CardContent>
        </Card>

        {/* Coverage Trend Chart */}
        <Card className="bg-slate-800 border-slate-700" data-testid="compliance-trend-tile">
          <CardHeader className="flex flex-row items-center justify-between pb-3">
            <div className="flex-1 min-w-0">
              <CardTitle className="text-lg text-white">Framework Coverage Trend</CardTitle>
              <p className="text-xs text-slate-400 mt-1">
                Weekly snapshots. Take a snapshot to seed the trend, then re-visit after a week.
              </p>
              {enabledFrameworks.length > 0 && (
                <div className="flex flex-wrap gap-1.5 mt-2" data-testid="trend-framework-chips">
                  {enabledFrameworks.map((fw) => {
                    const active = selectedFrameworkFilter.has(fw);
                    const label = frameworkLabels[fw] || fw;
                    return (
                      <button
                        key={fw}
                        type="button"
                        onClick={() => {
                          _updateFilter((prev) => {
                            const next = new Set(prev);
                            if (next.has(fw)) next.delete(fw); else next.add(fw);
                            return next;
                          });
                        }}
                        className={`text-[11px] px-2 py-0.5 rounded-full border transition ${
                          active
                            ? 'bg-indigo-600 border-indigo-500 text-white'
                            : 'bg-slate-900 border-slate-700 text-slate-300 hover:border-indigo-500 hover:text-white'
                        }`}
                        data-testid={`trend-framework-chip-${fw}`}
                        title={fw === label ? undefined : `key: ${fw}`}
                      >
                        {label}
                      </button>
                    );
                  })}
                </div>
              )}
            </div>
            <div className="flex items-center gap-2">
              <select
                data-testid="trend-framework-select"
                value={trendFramework}
                onChange={(e) => setTrendFramework(e.target.value)}
                className="bg-slate-900 border border-slate-700 rounded px-3 py-1.5 text-xs text-white"
              >
                <option value="">All Frameworks (top 5)</option>
                {Object.entries(coverageTrend).map(([fname, series]) => (
                  <option key={fname} value={fname}>
                    {(series[0] && series[0].framework) || fname} {(series[0] && series[0].version) ? `v${series[0].version}` : ''}
                  </option>
                ))}
              </select>
              <Button
                size="sm"
                variant="outline"
                data-testid="take-coverage-snapshot-btn"
                onClick={takeCoverageSnapshot}
                disabled={snapshottingCoverage}
                className="border-slate-600 text-slate-200 hover:bg-slate-700"
              >
                {snapshottingCoverage ? 'Snapping…' : 'Take Snapshot Now'}
              </Button>
            </div>
          </CardHeader>
          <CardContent>
            {Object.keys(coverageTrend).length === 0 ? (
              <div className="text-slate-500 text-sm py-6 text-center" data-testid="trend-empty">
                No snapshots yet. Click &quot;Take Snapshot Now&quot; to start tracking coverage over time.
              </div>
            ) : (() => {
              const normalise = (s) => String(s || '').toUpperCase().replace(/[^A-Z0-9]/g, '');
              const chipActive = selectedFrameworkFilter.size > 0
                ? new Set([...selectedFrameworkFilter].map(normalise))
                : null;
              // Filter the raw trend map to only frameworks selected by chip.
              const trendFiltered = chipActive
                ? Object.fromEntries(
                    Object.entries(coverageTrend).filter(([fname, series]) => {
                      const fw = (series[0] && series[0].framework) || fname;
                      return chipActive.has(normalise(fw)) || chipActive.has(normalise(fname));
                    })
                  )
                : coverageTrend;
              // Build data: array of {date, [framework]: pct, ...}
              const framesToShow = trendFramework
                ? [trendFramework]
                : Object.keys(trendFiltered).slice(0, 5);
              if (framesToShow.length === 0) {
                return (
                  <div className="text-slate-500 text-sm py-6 text-center" data-testid="trend-filtered-empty">
                    No snapshots for the selected frameworks yet.
                  </div>
                );
              }
              const byDate = {};
              framesToShow.forEach((fname) => {
                (trendFiltered[fname] || coverageTrend[fname] || []).forEach((p) => {
                  byDate[p.snapshot_date] = byDate[p.snapshot_date] || { date: p.snapshot_date };
                  const label = (coverageTrend[fname][0] && coverageTrend[fname][0].framework) || fname;
                  byDate[p.snapshot_date][label] = p.coverage_pct;
                });
              });
              const chartData = Object.values(byDate).sort((a, b) => a.date.localeCompare(b.date));
              const seriesKeys = Array.from(new Set(chartData.flatMap((d) => Object.keys(d).filter((k) => k !== 'date'))));
              const palette = ['#818cf8', '#34d399', '#fbbf24', '#f87171', '#a78bfa', '#22d3ee'];
              return (
                <div style={{ width: '100%', height: 240 }}>
                  <ResponsiveContainer>
                    <LineChart data={chartData}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#334155" />
                      <XAxis
                        dataKey="date"
                        stroke="#94a3b8"
                        fontSize={11}
                        tickFormatter={(v) => {
                          // Auto-plot snapshots write "YYYY-MM-DDTHH:MM" while the
                          // legacy manual snapshots write "YYYY-MM-DD". Render only
                          // the date portion so the axis stays readable.
                          const s = String(v || '');
                          return s.length >= 10 ? s.slice(0, 10) : s;
                        }}
                      />
                      <YAxis stroke="#94a3b8" fontSize={11} domain={[0, 100]} tickFormatter={(v) => `${v}%`} />
                      <ReTooltip contentStyle={{ background: '#0f172a', border: '1px solid #334155', color: '#e2e8f0', fontSize: 12 }} />
                      <Legend wrapperStyle={{ fontSize: 11, color: '#cbd5e1' }} />
                      {seriesKeys.map((k, i) => (
                        <Line key={k} type="monotone" dataKey={k} stroke={palette[i % palette.length]} strokeWidth={2} dot={{ r: 3 }} />
                      ))}
                    </LineChart>
                  </ResponsiveContainer>
                </div>
              );
            })()}
          </CardContent>
        </Card>

        {/* Provider Cards */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {[
            { provider: 'AWS', icon: '☁️', color: 'from-orange-500/20 to-orange-600/10', borderColor: 'border-orange-500/30', findings: providerBreakdown.aws || 0 },
            { provider: 'Azure', icon: '🔷', color: 'from-blue-500/20 to-blue-600/10', borderColor: 'border-blue-500/30', findings: providerBreakdown.azure || 0 },
            { provider: 'GCP', icon: '🌐', color: 'from-red-500/20 to-red-600/10', borderColor: 'border-red-500/30', findings: providerBreakdown.gcp || 0 },
          ].map((p) => {
            const accounts = stats.account_summaries?.filter(a => a.provider === p.provider.toLowerCase()) || [];
            // Real numbers from the backend — every completed scan's
            // summary.total_checks / passed_checks / failed_checks rolled up
            // by the account provider. Falls back to the account count from
            // ``account_summaries`` when the scan aggregation is empty (e.g.
            // provider has accounts but no completed scan yet).
            const providerStats = stats.checks_by_provider?.[p.provider.toLowerCase()] || {};
            const accountCount = providerStats.accounts ?? accounts.length;
            const totalChecks = providerStats.total_checks ?? 0;
            const failCount = providerStats.failed_checks ?? 0;
            const passCount = providerStats.passed_checks ?? 0;

            return (
              <Card key={p.provider} className={`bg-gradient-to-br ${p.color} border ${p.borderColor}`} data-testid={`provider-card-${p.provider.toLowerCase()}`}>
                <CardContent className="pt-6">
                  <div className="flex items-center justify-between mb-4">
                    <div className="flex items-center gap-3">
                      <span className="text-3xl">{p.icon}</span>
                      <div>
                        <h3 className="font-semibold text-white">{p.provider}</h3>
                        <p className="text-xs text-slate-400" data-testid={`provider-card-${p.provider.toLowerCase()}-accounts`}>{accountCount} Accounts</p>
                      </div>
                    </div>
                  </div>
                  <div className="grid grid-cols-3 gap-4 text-center">
                    <div>
                      <p className="text-2xl font-bold text-white" data-testid={`provider-card-${p.provider.toLowerCase()}-total`}>{totalChecks}</p>
                      <p className="text-xs text-slate-400">Checks</p>
                    </div>
                    <div>
                      <p className="text-2xl font-bold text-red-400" data-testid={`provider-card-${p.provider.toLowerCase()}-failed`}>{failCount}</p>
                      <p className="text-xs text-slate-400">Failed</p>
                    </div>
                    <div>
                      <p className="text-2xl font-bold text-emerald-400" data-testid={`provider-card-${p.provider.toLowerCase()}-passed`}>{passCount}</p>
                      <p className="text-xs text-slate-400">Passed</p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>

        {/* Risk Pipeline (Sankey-style visualization) */}
        <Card className="bg-slate-800 border-slate-700">
          <CardHeader>
            <CardTitle className="text-lg text-white flex items-center gap-2">
              <TrendingUp className="w-5 h-5 text-emerald-500" />
              Risk Pipeline
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex items-center justify-between h-48">
              {/* Source - Providers */}
              <div className="w-32 space-y-2">
                {Object.entries(providerBreakdown).map(([provider, count]) => (
                  <div key={provider} className="flex items-center gap-2">
                    <span className="text-lg">{providerIcons[provider] || '☁️'}</span>
                    <div>
                      <p className="text-xs text-slate-400 uppercase">{provider}</p>
                      <p className="text-sm font-medium text-white">{Math.round(count)}</p>
                    </div>
                  </div>
                ))}
              </div>
              
              {/* Flow Lines */}
              <div className="flex-1 relative h-full mx-8">
                <svg className="w-full h-full" viewBox="0 0 400 150" preserveAspectRatio="none">
                  {/* Critical flow */}
                  <path d="M0,20 C100,20 300,15 400,15" stroke="#f472b6" strokeWidth="4" fill="none" opacity="0.6" />
                  {/* High flow */}
                  <path d="M0,50 C100,50 300,45 400,45" stroke="#f97316" strokeWidth="8" fill="none" opacity="0.6" />
                  {/* Medium flow */}
                  <path d="M0,80 C100,80 300,85 400,85" stroke="#eab308" strokeWidth="6" fill="none" opacity="0.6" />
                  {/* Low flow */}
                  <path d="M0,110 C100,110 300,120 400,120" stroke="#94a3b8" strokeWidth="3" fill="none" opacity="0.6" />
                </svg>
              </div>
              
              {/* Destination - Severity */}
              <div className="w-36 space-y-2">
                {[
                  { label: 'Critical', count: severityData.critical, color: 'bg-pink-500' },
                  { label: 'High', count: severityData.high, color: 'bg-orange-500' },
                  { label: 'Medium', count: severityData.medium, color: 'bg-yellow-500' },
                  { label: 'Low', count: severityData.low, color: 'bg-slate-400' },
                ].map((s) => (
                  <div key={s.label} className="flex items-center gap-2">
                    <div className={`w-3 h-3 rounded ${s.color}`} />
                    <span className="text-xs text-slate-400">{s.label}</span>
                    <span className="text-sm font-medium text-white ml-auto">{s.count}</span>
                  </div>
                ))}
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Latest Failing Findings */}
        <Card className="bg-slate-800 border-slate-700">
          <CardHeader className="flex flex-row items-center justify-between">
            <CardTitle className="text-lg text-white flex items-center gap-2">
              <AlertTriangle className="w-5 h-5 text-orange-500" />
              Latest Failing Findings
            </CardTitle>
            <Button
              variant="ghost"
              size="sm"
              onClick={() => navigate(`${basePath}/scans`)}
              className="text-emerald-400 hover:text-emerald-300"
            >
              View All Findings
              <ArrowRight className="w-4 h-4 ml-2" />
            </Button>
          </CardHeader>
          <CardContent>
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b border-slate-700">
                    <th className="text-left py-3 px-4 text-xs font-medium text-slate-400 uppercase">Details</th>
                    <th className="text-left py-3 px-4 text-xs font-medium text-slate-400 uppercase">Finding</th>
                    <th className="text-left py-3 px-4 text-xs font-medium text-slate-400 uppercase">Resource</th>
                    <th className="text-left py-3 px-4 text-xs font-medium text-slate-400 uppercase">Severity</th>
                    <th className="text-left py-3 px-4 text-xs font-medium text-slate-400 uppercase">Status</th>
                    <th className="text-left py-3 px-4 text-xs font-medium text-slate-400 uppercase">Region</th>
                    <th className="text-left py-3 px-4 text-xs font-medium text-slate-400 uppercase">Service</th>
                    <th className="text-left py-3 px-4 text-xs font-medium text-slate-400 uppercase">Count</th>
                  </tr>
                </thead>
                <tbody>
                  {latestFindings.length === 0 ? (
                    <tr>
                      <td colSpan={8} className="py-8 text-center" data-testid="latest-findings-empty">
                        <p className="text-slate-500 text-sm">
                          No failing findings yet. Run a scan to surface issues here.
                        </p>
                      </td>
                    </tr>
                  ) : latestFindings.map((finding, idx) => (
                    <React.Fragment key={finding.id || finding.check_id || idx}>
                      <tr 
                        className="border-b border-slate-700/50 hover:bg-slate-700/30 cursor-pointer"
                        onClick={() => setExpandedFinding(expandedFinding === idx ? null : idx)}
                        data-testid={`finding-row-${idx}`}
                      >
                        <td className="py-3 px-4">
                          <button className="p-1 hover:bg-slate-600 rounded">
                            {expandedFinding === idx ? (
                              <ChevronDown className="w-4 h-4 text-slate-400" />
                            ) : (
                              <ChevronRight className="w-4 h-4 text-slate-400" />
                            )}
                          </button>
                        </td>
                        <td className="py-3 px-4 text-sm text-white max-w-xs truncate">
                          {finding.title || finding.name}
                        </td>
                        <td className="py-3 px-4 text-xs text-slate-400 font-mono max-w-xs truncate" title={finding.resource}>
                          {finding.resource || finding.resource_id || '—'}
                        </td>
                        <td className="py-3 px-4">
                          <Badge className={`
                            ${finding.severity === 'critical' ? 'bg-pink-500/20 text-pink-400 border-pink-500/30' : ''}
                            ${finding.severity === 'high' ? 'bg-orange-500/20 text-orange-400 border-orange-500/30' : ''}
                            ${finding.severity === 'medium' ? 'bg-yellow-500/20 text-yellow-400 border-yellow-500/30' : ''}
                            ${finding.severity === 'low' ? 'bg-slate-500/20 text-slate-400 border-slate-500/30' : ''}
                          `}>
                            {finding.severity || 'unknown'}
                          </Badge>
                        </td>
                        <td className="py-3 px-4">
                          <Badge variant="outline" className="text-red-400 border-red-400/30">
                            {(finding.status || 'fail').toUpperCase()}
                          </Badge>
                        </td>
                        <td className="py-3 px-4 text-xs text-slate-400">
                          {finding.region || 'global'}
                        </td>
                        <td className="py-3 px-4 text-xs text-slate-400 uppercase">
                          {finding.service || '—'}
                        </td>
                        <td className="py-3 px-4 text-xs text-slate-300 font-semibold">
                          {finding.count || 1}
                        </td>
                      </tr>
                      {/* Expanded Row */}
                      {expandedFinding === idx && (
                        <tr className="bg-slate-700/20">
                          <td colSpan={8} className="p-4">
                            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                              <div className="space-y-2">
                                <h4 className="text-xs font-medium text-slate-400 uppercase">Resource Details</h4>
                                <p className="text-sm text-white font-mono break-all">
                                  {finding.resource || finding.resource_id || '—'}
                                </p>
                                <div className="grid grid-cols-2 gap-2 mt-2">
                                  <div>
                                    <p className="text-[10px] uppercase text-slate-500">Region</p>
                                    <p className="text-xs text-slate-300">{finding.region || 'global'}</p>
                                  </div>
                                  <div>
                                    <p className="text-[10px] uppercase text-slate-500">Service</p>
                                    <p className="text-xs text-slate-300 uppercase">{finding.service || '—'}</p>
                                  </div>
                                  <div>
                                    <p className="text-[10px] uppercase text-slate-500">Provider</p>
                                    <p className="text-xs text-slate-300 uppercase">{finding.provider || '—'}</p>
                                  </div>
                                  <div>
                                    <p className="text-[10px] uppercase text-slate-500">Account</p>
                                    <p className="text-xs text-slate-300 truncate" title={finding.account_native_id}>
                                      {finding.account_name || finding.account_native_id || '—'}
                                    </p>
                                  </div>
                                </div>
                              </div>
                              <div className="space-y-2">
                                <h4 className="text-xs font-medium text-slate-400 uppercase">Description</h4>
                                <p className="text-sm text-slate-300">
                                  {finding.description || 'This security finding indicates a potential vulnerability or misconfiguration that should be addressed to improve your cloud security posture.'}
                                </p>
                                {finding.risk && (
                                  <>
                                    <h4 className="text-xs font-medium text-slate-400 uppercase mt-3">Risk</h4>
                                    <p className="text-sm text-amber-300">{finding.risk}</p>
                                  </>
                                )}
                              </div>
                              <div className="space-y-2">
                                <h4 className="text-xs font-medium text-slate-400 uppercase">Remediation</h4>
                                <p className="text-sm text-emerald-400">
                                  {finding.remediation || 'Review and update the resource configuration to align with security best practices.'}
                                </p>
                                <div className="flex flex-wrap gap-2 mt-2">
                                  <Button
                                    size="sm"
                                    variant="outline"
                                    className="text-emerald-400 border-emerald-400/30"
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      if (finding.scan_id) {
                                        navigate(`${basePath}/scans/${finding.scan_id}`);
                                      } else {
                                        navigate(`${basePath}/scans`);
                                      }
                                    }}
                                    data-testid={`finding-view-full-${idx}`}
                                  >
                                    <FileText className="w-4 h-4 mr-2" />
                                    View Full Details
                                  </Button>
                                  {(finding.check_id || '').toUpperCase() === 'AWS-STS-ASSUME-ROLE-FAILED' && (
                                    <Button
                                      size="sm"
                                      className="bg-emerald-600 hover:bg-emerald-700 text-white"
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        if (finding.account_id) {
                                          navigate(`${basePath}/accounts?openWizard=${encodeURIComponent(finding.account_id)}`);
                                        } else {
                                          navigate(`${basePath}/accounts`);
                                        }
                                      }}
                                      data-testid={`finding-open-setup-wizard-${idx}`}
                                    >
                                      <Rocket className="w-4 h-4 mr-2" />
                                      Open Setup Wizard
                                    </Button>
                                  )}
                                </div>
                              </div>
                            </div>
                          </td>
                        </tr>
                      )}
                    </React.Fragment>
                  ))}
                </tbody>
              </table>
            </div>
          </CardContent>
        </Card>

        {/* Compliance by Framework */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <Card className="bg-slate-800 border-slate-700">
            <CardHeader>
              <CardTitle className="text-lg text-white flex items-center gap-2">
                <PieChart className="w-5 h-5 text-emerald-500" />
                Compliance by Framework
              </CardTitle>
              {stats.enabled_frameworks?.length > 0 && (
                <p className="text-xs text-slate-500 mt-1" data-testid="dashboard-enabled-frameworks">
                  Showing only your enabled frameworks: {stats.enabled_frameworks.join(', ')}
                </p>
              )}
            </CardHeader>
            <CardContent>
              {Object.keys(stats.compliance_by_framework || {}).length > 0 ? (
                <div className="space-y-4">
                  {Object.entries(stats.compliance_by_framework || {}).slice(0, 6).map(([framework, score]) => (
                    <div key={framework} className="flex items-center gap-4">
                      <div className="w-24 text-sm font-medium text-slate-300 truncate">
                        {frameworkShortNames[framework] || framework}
                      </div>
                      <div className="flex-1 h-2 bg-slate-700 rounded-full overflow-hidden">
                        <div 
                          className={`h-full rounded-full ${
                            score >= 80 ? 'bg-emerald-500' : 
                            score >= 60 ? 'bg-yellow-500' : 'bg-red-500'
                          }`}
                          style={{ width: `${score}%` }}
                        />
                      </div>
                      <div className="w-14 text-right text-sm font-medium text-white">
                        {score.toFixed(1)}%
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-8 text-slate-500">
                  <FileText className="w-12 h-12 mx-auto mb-2 text-slate-600" />
                  <p>Run a scan with compliance frameworks to see results</p>
                </div>
              )}
            </CardContent>
          </Card>

          {/* Recent Scans */}
          <Card className="bg-slate-800 border-slate-700">
            <CardHeader className="flex flex-row items-center justify-between">
              <CardTitle className="text-lg text-white flex items-center gap-2">
                <Activity className="w-5 h-5 text-emerald-500" />
                Recent Scans
              </CardTitle>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => navigate(`${basePath}/scans`)}
                className="text-emerald-400"
              >
                View All
              </Button>
            </CardHeader>
            <CardContent>
              <div className="space-y-3">
                {(stats.recent_scans || [])
                  .slice()
                  .sort((a, b) => new Date(b.created_at || 0) - new Date(a.created_at || 0))
                  .slice(0, 5).map((scan) => (
                  <div 
                    key={scan.id}
                    className="flex items-center justify-between p-3 bg-slate-700/50 rounded-lg hover:bg-slate-700 cursor-pointer transition-colors"
                    onClick={() => navigate(`${basePath}/scans/${scan.id}`)}
                  >
                    <div>
                      <p className="font-medium text-white">{scan.name}</p>
                      <p className="text-xs text-slate-400">
                        {new Date(scan.created_at).toLocaleString()}
                      </p>
                    </div>
                    <div className="flex items-center gap-3">
                      <Badge className={`
                        ${scan.status === 'completed' ? 'bg-emerald-500/20 text-emerald-400' : ''}
                        ${scan.status === 'running' ? 'bg-blue-500/20 text-blue-400' : ''}
                        ${scan.status === 'failed' ? 'bg-red-500/20 text-red-400' : ''}
                        ${scan.status === 'pending' ? 'bg-slate-500/20 text-slate-400' : ''}
                      `}>
                        {scan.status}
                      </Badge>
                      {scan.status === 'completed' && (
                        <Button
                          size="sm"
                          variant="outline"
                          className="text-xs border-emerald-500/30 text-emerald-400 hover:bg-emerald-500/10"
                          onClick={(e) => handleCollectEvidence(scan.id, e)}
                          disabled={collectingEvidence === scan.id}
                          data-testid={`collect-evidence-${scan.id}`}
                        >
                          {collectingEvidence === scan.id ? (
                            <RefreshCw className="w-3 h-3 mr-1 animate-spin" />
                          ) : (
                            <FileText className="w-3 h-3 mr-1" />
                          )}
                          Collect Evidence
                        </Button>
                      )}
                      <span className="text-sm text-white font-medium">
                        {scan.compliance_score?.toFixed(0) || 0}%
                      </span>
                    </div>
                  </div>
                ))}
                
                {(stats.recent_scans || []).length === 0 && (
                  <div className="text-center py-8 text-slate-500">
                    <Search className="w-12 h-12 mx-auto mb-2 text-slate-600" />
                    <p>No scans yet</p>
                    <Button
                      onClick={() => navigate(`${basePath}/scan`)}
                      className="mt-4 bg-emerald-600 hover:bg-emerald-700"
                    >
                      Run First Scan
                    </Button>
                  </div>
                )}
              </div>
            </CardContent>
          </Card>
        </div>
          </>
        )}

        {/* Collect Evidence Framework Mapping Modal — must live in this component to access state */}
        <EvidenceFrameworkModal
          evidenceModalScan={evidenceModalScan}
          availableFrameworks={availableFrameworks}
          selectedFrameworkCodes={selectedFrameworkCodes}
          loadingFrameworks={loadingFrameworks}
          collectingEvidence={collectingEvidence}
          toggleFrameworkSelection={toggleFrameworkSelection}
          setSelectedFrameworkCodes={setSelectedFrameworkCodes}
          closeEvidenceModal={closeEvidenceModal}
          confirmCollectEvidence={confirmCollectEvidence}
        />

        {/* Compliance Report Export Dialog */}
        {showComplianceExportDialog && (
          <div className="fixed inset-0 z-50 bg-black/60 flex items-center justify-center p-4" data-testid="compliance-export-modal">
            <div className="bg-slate-800 border border-slate-700 rounded-lg w-full max-w-lg p-6">
              <div className="flex items-start justify-between mb-4">
                <div>
                  <h3 className="text-lg font-semibold text-white">Export Compliance Report</h3>
                  <p className="text-xs text-slate-400 mt-1">
                    Choose a framework to generate an auditor-ready PDF for this tenant.
                  </p>
                </div>
                <button
                  onClick={() => setShowComplianceExportDialog(false)}
                  className="text-slate-400 hover:text-white text-xl leading-none"
                >
                  ×
                </button>
              </div>
              <label className="text-xs text-slate-400 mb-1 block">Framework</label>
              <select
                data-testid="compliance-export-framework-select"
                value={selectedFrameworkForPdf || ''}
                onChange={(e) => setSelectedFrameworkForPdf(e.target.value)}
                className="w-full bg-slate-900 border border-slate-700 rounded px-3 py-2 text-sm text-white"
              >
                <option value="">— Select a framework —</option>
                {complianceCoverage.map((c) => (
                  <option key={c.file_name} value={c.file_name}>
                    [{c.provider?.toUpperCase()}] {c.framework} {c.version ? `v${c.version}` : ''} — {c.coverage_pct}% coverage
                  </option>
                ))}
              </select>
              <div className="mt-5 flex justify-end gap-2">
                <Button variant="outline" onClick={() => setShowComplianceExportDialog(false)}>Cancel</Button>
                <Button
                  data-testid="compliance-export-confirm-btn"
                  onClick={exportComplianceReportPdf}
                  disabled={!selectedFrameworkForPdf || exportingComplianceReport}
                >
                  {exportingComplianceReport ? 'Generating…' : 'Download PDF'}
                </Button>
              </div>
            </div>
          </div>
        )}

        {/* Framework Rename Dialog (super-admin only) */}
        {showRenameDialog && (
          <div className="fixed inset-0 z-50 bg-black/60 flex items-center justify-center p-4" data-testid="framework-rename-modal">
            <div className="bg-slate-800 border border-slate-700 rounded-lg w-full max-w-lg p-6">
              <div className="flex items-start justify-between mb-4">
                <div>
                  <h3 className="text-lg font-semibold text-white">Rename Framework Labels</h3>
                  <p className="text-xs text-slate-400 mt-1">
                    Give each framework a friendlier display name (e.g. &quot;SOC 2 Type II&quot;). The underlying framework key is preserved. Leave blank to remove an override.
                  </p>
                </div>
                <button
                  onClick={() => setShowRenameDialog(false)}
                  className="text-slate-400 hover:text-white text-xl leading-none"
                  data-testid="framework-rename-close"
                >
                  ×
                </button>
              </div>
              <div className="max-h-96 overflow-y-auto space-y-2">
                {enabledFrameworks.map((fw) => (
                  <div key={fw} className="flex items-center gap-2">
                    <span className="text-xs font-mono text-slate-400 w-24 shrink-0 truncate" title={fw}>{fw}</span>
                    <input
                      type="text"
                      value={renameDraft[fw] || ''}
                      placeholder={fw}
                      onChange={(e) => setRenameDraft((d) => ({ ...d, [fw]: e.target.value }))}
                      className="flex-1 bg-slate-900 border border-slate-700 rounded px-2 py-1 text-sm text-white"
                      data-testid={`framework-rename-input-${fw}`}
                    />
                  </div>
                ))}
              </div>
              <div className="mt-5 flex justify-end gap-2">
                <Button variant="outline" onClick={() => setShowRenameDialog(false)}>Cancel</Button>
                <Button
                  data-testid="framework-rename-save"
                  disabled={savingLabels}
                  onClick={async () => {
                    if (!tenant?.id) return;
                    try {
                      setSavingLabels(true);
                      const res = await complianceAPI.updateFrameworkLabels(tenant.id, renameDraft);
                      setFrameworkLabels(res.data.labels || {});
                      toast.success('Framework labels saved');
                      setShowRenameDialog(false);
                    } catch (err) {
                      toast.error(err.response?.data?.detail || 'Failed to save labels');
                    } finally { setSavingLabels(false); }
                  }}
                >
                  {savingLabels ? 'Saving…' : 'Save Labels'}
                </Button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

// Cloud Configuration Guide Component
const CloudConfigurationGuide = ({ onEnableDemo }) => {
  const [selectedProvider, setSelectedProvider] = useState('aws');
  const navigate = useNavigateHelper();
  const location = useLocation();
  const basePath = location.pathname.startsWith('/amidala') ? '/amidala' : '/grc/amidala';

  const providers = [
    { id: 'aws', name: 'Amazon Web Services', icon: '☁️', color: 'orange' },
    { id: 'azure', name: 'Microsoft Azure', icon: '☁️', color: 'blue' },
    { id: 'gcp', name: 'Google Cloud Platform', icon: '☁️', color: 'green' },
    { id: 'onprem', name: 'On-Premises', icon: '🏢', color: 'purple' }
  ];

  const configGuides = {
    aws: {
      title: 'AWS Configuration',
      subtitle: 'Connect your AWS accounts for security scanning',
      accountId: '9925-5878-5763',
      steps: [
        {
          title: 'Create an IAM Role',
          description: 'Create a new IAM role in your AWS account with cross-account access. Use Beskar Account ID: 992558785763',
          code: `aws iam create-role --role-name BeskarSecurityRole \\
  --assume-role-policy-document file://trust-policy.json`
        },
        {
          title: 'Attach SecurityAudit Policy',
          description: 'Attach the AWS managed SecurityAudit policy for read-only security scanning.',
          code: `aws iam attach-role-policy --role-name BeskarSecurityRole \\
  --policy-arn arn:aws:iam::aws:policy/SecurityAudit`
        },
        {
          title: 'Configure Trust Relationship',
          description: 'Allow Beskar (Account ID: 992558785763) to assume the role.',
          code: `{
  "Version": "2012-10-17",
  "Statement": [{
    "Effect": "Allow",
    "Principal": { "AWS": "arn:aws:iam::992558785763:root" },
    "Action": "sts:AssumeRole",
    "Condition": { "StringEquals": { "sts:ExternalId": "YOUR_EXTERNAL_ID" } }
  }]
}`
        },
        {
          title: 'Enable CloudTrail',
          description: 'Enable CloudTrail in all regions for comprehensive logging.',
          code: `aws cloudtrail create-trail --name beskar-audit-trail \\
  --s3-bucket-name your-audit-bucket --is-multi-region-trail`
        }
      ],
      docUrl: 'https://docs.aws.amazon.com/IAM/latest/UserGuide/id_roles_create_for-user.html'
    },
    azure: {
      title: 'Azure Configuration',
      subtitle: 'Connect your Azure subscriptions for security scanning',
      steps: [
        {
          title: 'Register an Application',
          description: 'Create an App registration in Azure Active Directory.',
          code: `az ad app create --display-name "Beskar Security Scanner" \\
  --sign-in-audience AzureADMyOrg`
        },
        {
          title: 'Create a Service Principal',
          description: 'Create a service principal for the application.',
          code: `az ad sp create --id <APP_ID>`
        },
        {
          title: 'Generate Client Secret',
          description: 'Create a client secret for authentication.',
          code: `az ad app credential reset --id <APP_ID> --years 2`
        },
        {
          title: 'Assign Reader Role',
          description: 'Grant Reader role at subscription level for security scanning.',
          code: `az role assignment create --assignee <SP_ID> \\
  --role "Reader" --scope /subscriptions/<SUBSCRIPTION_ID>`
        },
        {
          title: 'Note Your Credentials',
          description: 'Save these values for configuration:',
          code: `Tenant ID: <your-tenant-id>
Client ID: <your-app-id>
Client Secret: <your-secret>
Subscription ID: <your-subscription-id>`
        }
      ],
      docUrl: 'https://docs.microsoft.com/en-us/azure/active-directory/develop/quickstart-register-app'
    },
    gcp: {
      title: 'GCP Configuration',
      subtitle: 'Connect your Google Cloud projects for security scanning',
      steps: [
        {
          title: 'Create a Service Account',
          description: 'Create a new service account in your GCP project.',
          code: `gcloud iam service-accounts create beskar-scanner \\
  --display-name="Beskar Security Scanner" \\
  --description="Service account for cloud security scanning"`
        },
        {
          title: 'Grant Security Reviewer Role',
          description: 'Assign the Security Reviewer role for read-only access.',
          code: `gcloud projects add-iam-policy-binding PROJECT_ID \\
  --member="serviceAccount:beskar-scanner@PROJECT_ID.iam.gserviceaccount.com" \\
  --role="roles/iam.securityReviewer"`
        },
        {
          title: 'Enable Required APIs',
          description: 'Enable Cloud Asset API and Security Command Center API.',
          code: `gcloud services enable cloudasset.googleapis.com
gcloud services enable securitycenter.googleapis.com
gcloud services enable cloudresourcemanager.googleapis.com`
        },
        {
          title: 'Generate Service Account Key',
          description: 'Create and download the JSON key file.',
          code: `gcloud iam service-accounts keys create key.json \\
  --iam-account=beskar-scanner@PROJECT_ID.iam.gserviceaccount.com`
        }
      ],
      docUrl: 'https://cloud.google.com/iam/docs/creating-managing-service-accounts'
    },
    onprem: {
      title: 'On-Premises Configuration',
      subtitle: 'Deploy agents for on-premises infrastructure scanning',
      steps: [
        {
          title: 'Download the Beskar Agent',
          description: 'Install the security scanning agent on your servers.',
          code: `curl -sSL https://beskar.io/agent/install.sh | bash`
        },
        {
          title: 'Configure Agent Settings',
          description: 'Edit the agent configuration file.',
          code: `# /etc/beskar-agent/config.yaml
api_key: YOUR_API_KEY
tenant_id: YOUR_TENANT_ID
scan_interval: 3600
modules:
  - vulnerability
  - compliance
  - inventory`
        },
        {
          title: 'Configure Network Access',
          description: 'Ensure the agent can reach your infrastructure.',
          code: `# For Linux servers (SSH)
ssh_user: beskar-scanner
ssh_key_path: /etc/beskar-agent/id_rsa

# For Windows servers (WinRM)
winrm_user: beskar-scanner
winrm_password_vault: azure-keyvault://beskar-secrets`
        },
        {
          title: 'Start the Agent',
          description: 'Start the agent service and verify registration.',
          code: `sudo systemctl enable beskar-agent
sudo systemctl start beskar-agent
sudo beskar-agent status`
        }
      ],
      docUrl: 'https://docs.beskar.io/agent/installation'
    }
  };

  const currentGuide = configGuides[selectedProvider];
  const currentProviderInfo = providers.find(p => p.id === selectedProvider);

  const colorClasses = {
    orange: 'bg-orange-500/10 border-orange-500/30 text-orange-400',
    blue: 'bg-blue-500/10 border-blue-500/30 text-blue-400',
    green: 'bg-green-500/10 border-green-500/30 text-green-400',
    purple: 'bg-purple-500/10 border-purple-500/30 text-purple-400'
  };

  const buttonColorClasses = {
    orange: 'bg-orange-600 hover:bg-orange-700',
    blue: 'bg-blue-600 hover:bg-blue-700',
    green: 'bg-green-600 hover:bg-green-700',
    purple: 'bg-purple-600 hover:bg-purple-700'
  };

  return (
    <div className="space-y-6" data-testid="cloud-config-guide">
      {/* Header */}
      <div className="text-center py-8">
        <div className="w-20 h-20 bg-emerald-500/20 rounded-2xl flex items-center justify-center mx-auto mb-4">
          <Cloud className="w-12 h-12 text-emerald-500" />
        </div>
        <h2 className="text-2xl font-bold text-white mb-2">Configure Cloud Security Scanning</h2>
        <p className="text-slate-400 max-w-2xl mx-auto">
          Connect your cloud accounts to start scanning for misconfigurations, vulnerabilities, and compliance issues.
          Follow the setup guide for your cloud provider below.
        </p>
        <Button onClick={onEnableDemo} variant="outline" className="mt-4 border-slate-600 text-slate-300 hover:bg-slate-700">
          <Play className="w-4 h-4 mr-2" />
          Try Demo Mode Instead
        </Button>
      </div>

      {/* Provider Selection */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        {providers.map(provider => (
          <button
            key={provider.id}
            onClick={() => setSelectedProvider(provider.id)}
            className={`p-4 rounded-xl border-2 transition-all ${
              selectedProvider === provider.id
                ? colorClasses[provider.color]
                : 'bg-slate-800 border-slate-700 text-slate-400 hover:border-slate-600'
            }`}
            data-testid={`provider-btn-${provider.id}`}
          >
            <div className="text-3xl mb-2">{provider.icon}</div>
            <p className="font-medium text-sm">{provider.name}</p>
          </button>
        ))}
      </div>

      {/* Configuration Guide */}
      <Card className="bg-slate-800 border-slate-700">
        <CardHeader className={`border-b border-slate-700 ${colorClasses[currentProviderInfo.color].replace('border-', 'bg-').split(' ')[0]}`}>
          <CardTitle className="text-xl text-white flex items-center gap-3">
            <span className="text-2xl">{currentProviderInfo.icon}</span>
            {currentGuide.title}
          </CardTitle>
          <p className="text-slate-400 text-sm mt-1">{currentGuide.subtitle}</p>
        </CardHeader>
        <CardContent className="pt-6 space-y-6">
          {currentGuide.steps.map((step, index) => (
            <div key={index} className="relative pl-8">
              <div className={`absolute left-0 top-0 w-6 h-6 rounded-full flex items-center justify-center text-xs font-bold ${colorClasses[currentProviderInfo.color]}`}>
                {index + 1}
              </div>
              <div>
                <h4 className="font-semibold text-white mb-1">{step.title}</h4>
                <p className="text-sm text-slate-400 mb-3">{step.description}</p>
                <pre className="p-4 bg-slate-900 rounded-lg text-sm text-slate-300 overflow-x-auto border border-slate-700">
                  <code>{step.code}</code>
                </pre>
              </div>
            </div>
          ))}

          {/* Documentation Link */}
          <div className="flex items-center justify-between pt-4 border-t border-slate-700">
            <a
              href={currentGuide.docUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="text-sm text-emerald-400 hover:text-emerald-300 flex items-center gap-1"
            >
              <FileText className="w-4 h-4" />
              View Official Documentation
              <ArrowRight className="w-4 h-4" />
            </a>
            <Button
              onClick={() => navigate(`${basePath}/accounts`)}
              className={buttonColorClasses[currentProviderInfo.color]}
            >
              <Plus className="w-4 h-4 mr-2" />
              Add {currentProviderInfo.name} Account
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Quick Tips */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <Card className="bg-slate-800 border-slate-700">
          <CardContent className="pt-6">
            <div className="flex items-start gap-3">
              <div className="w-10 h-10 bg-emerald-500/20 rounded-lg flex items-center justify-center">
                <Shield className="w-5 h-5 text-emerald-500" />
              </div>
              <div>
                <h4 className="font-semibold text-white mb-1">Least Privilege</h4>
                <p className="text-sm text-slate-400">
                  We only request read-only permissions. Your data stays secure.
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card className="bg-slate-800 border-slate-700">
          <CardContent className="pt-6">
            <div className="flex items-start gap-3">
              <div className="w-10 h-10 bg-blue-500/20 rounded-lg flex items-center justify-center">
                <Activity className="w-5 h-5 text-blue-500" />
              </div>
              <div>
                <h4 className="font-semibold text-white mb-1">Continuous Monitoring</h4>
                <p className="text-sm text-slate-400">
                  Automatic scans detect new issues as your infrastructure changes.
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card className="bg-slate-800 border-slate-700">
          <CardContent className="pt-6">
            <div className="flex items-start gap-3">
              <div className="w-10 h-10 bg-purple-500/20 rounded-lg flex items-center justify-center">
                <BarChart3 className="w-5 h-5 text-purple-500" />
              </div>
              <div>
                <h4 className="font-semibold text-white mb-1">Compliance Ready</h4>
                <p className="text-sm text-slate-400">
                  Map findings to CIS, SOC 2, PCI-DSS, HIPAA, and more.
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

// Modal extracted so it can be rendered both inside the dashboard tree AND
// during the full-screen loading state — so it survives background refreshes.
const EvidenceFrameworkModal = ({
  evidenceModalScan,
  availableFrameworks,
  selectedFrameworkCodes,
  loadingFrameworks,
  collectingEvidence,
  toggleFrameworkSelection,
  setSelectedFrameworkCodes,
  closeEvidenceModal,
  confirmCollectEvidence,
}) => {
  if (!evidenceModalScan) return null;
  return (
    <div
      className="fixed inset-0 z-50 bg-black/60 flex items-center justify-center p-4"
      data-testid="collect-evidence-modal"
      onClick={closeEvidenceModal}
    >
      <div
        className="bg-slate-900 border border-slate-700 rounded-xl shadow-2xl w-full max-w-2xl max-h-[90vh] overflow-hidden flex flex-col"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between p-5 border-b border-slate-700">
          <div>
            <h3 className="text-lg font-bold text-white flex items-center gap-2">
              <FileText className="w-5 h-5 text-emerald-400" />
              Map Evidence to Frameworks
            </h3>
            <p className="text-xs text-slate-400 mt-1">
              Scan: <span className="text-slate-200">{evidenceModalScan.name}</span>
            </p>
          </div>
          <button
            onClick={closeEvidenceModal}
            disabled={!!collectingEvidence}
            className="p-1.5 rounded hover:bg-slate-800 text-slate-400 hover:text-white disabled:opacity-50"
            data-testid="collect-evidence-modal-close"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="p-5 overflow-y-auto">
          <p className="text-sm text-slate-300 mb-4">
            Select which of your enabled frameworks should receive auto-generated evidence
            records linked to GRC controls. Unselected frameworks will still appear in the
            evidence's <code className="text-emerald-400">mapped_frameworks</code> field but
            no GRC control links will be created.
          </p>

          {loadingFrameworks ? (
            <div className="flex items-center justify-center py-8 text-slate-400">
              <RefreshCw className="w-5 h-5 animate-spin mr-2" /> Loading frameworks…
            </div>
          ) : availableFrameworks.length === 0 ? (
            <div className="text-center py-8 text-slate-400">
              <AlertCircle className="w-10 h-10 mx-auto mb-2 text-amber-500" />
              <p>No GRC frameworks enabled yet.</p>
              <p className="text-xs mt-1">
                Enable a framework under GRC → Frameworks first, then collect evidence.
              </p>
            </div>
          ) : (
            <>
              <div className="flex items-center justify-between mb-3">
                <p className="text-xs text-slate-400">
                  {selectedFrameworkCodes.length}/{availableFrameworks.length} selected
                </p>
                <div className="flex gap-2">
                  <button
                    type="button"
                    onClick={() =>
                      setSelectedFrameworkCodes(availableFrameworks.map((f) => f.code))
                    }
                    className="text-xs text-emerald-400 hover:text-emerald-300"
                    data-testid="frameworks-select-all"
                  >
                    Select all
                  </button>
                  <button
                    type="button"
                    onClick={() => setSelectedFrameworkCodes([])}
                    className="text-xs text-slate-400 hover:text-slate-200"
                    data-testid="frameworks-clear"
                  >
                    Clear
                  </button>
                </div>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 max-h-96 overflow-y-auto pr-1">
                {availableFrameworks.map((fw) => {
                  const code = fw.code;
                  const isOn = selectedFrameworkCodes.includes(code);
                  return (
                    <button
                      type="button"
                      key={fw.id || code}
                      onClick={() => toggleFrameworkSelection(code)}
                      className={`flex items-start gap-3 p-3 rounded-lg border text-left transition-all ${
                        isOn
                          ? 'bg-emerald-500/10 border-emerald-500/40'
                          : 'bg-slate-800/60 border-slate-700 hover:border-slate-600'
                      }`}
                      data-testid={`framework-option-${code}`}
                    >
                      <div
                        className={`w-4 h-4 mt-0.5 rounded border flex items-center justify-center flex-shrink-0 ${
                          isOn ? 'bg-emerald-500 border-emerald-500' : 'border-slate-500'
                        }`}
                      >
                        {isOn && <CheckCircle className="w-3 h-3 text-white" />}
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium text-white truncate">
                          {fw.name || code}
                        </p>
                        <p className="text-xs text-slate-400 truncate">{code}</p>
                      </div>
                    </button>
                  );
                })}
              </div>
            </>
          )}
        </div>

        <div className="p-5 border-t border-slate-700 flex justify-end gap-3">
          <Button
            variant="outline"
            onClick={closeEvidenceModal}
            disabled={!!collectingEvidence}
            className="border-slate-600 text-slate-200 hover:bg-slate-800"
            data-testid="collect-evidence-cancel"
          >
            Cancel
          </Button>
          <Button
            onClick={confirmCollectEvidence}
            disabled={!!collectingEvidence || loadingFrameworks || availableFrameworks.length === 0}
            className="bg-emerald-600 hover:bg-emerald-700 text-white"
            data-testid="collect-evidence-confirm"
          >
            {collectingEvidence ? (
              <>
                <RefreshCw className="w-4 h-4 mr-2 animate-spin" /> Collecting…
              </>
            ) : (
              <>
                <FileText className="w-4 h-4 mr-2" /> Collect & Map
              </>
            )}
          </Button>
        </div>
      </div>
    </div>
  );
};

export default AmidalaDashboard;
